// MoveToFolderModal — files one or many form templates into a folder. Serves BOTH
// entry points (the row kebab and the selection bar) because the endpoint takes an
// id array; the page decides which rows to hand over.
//
// Presentational, like people-page's StructureBulkModal: it owns only the picker
// state and hands the chosen folder back through onConfirm.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal } from '@/components/common';
import type { FormTemplate } from '@/api/endpoints/formsApi';
import type { FormFolder } from '@/api/endpoints/formFoldersApi';

interface MoveToFolderModalProps {
  open: boolean;
  /** The forms being moved — drives the subtitle and the preselection. */
  forms: FormTemplate[];
  folders: FormFolder[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (folder: string | null) => void;
}

const MoveToFolderModal = ({
  open,
  forms,
  folders,
  loading = false,
  onClose,
  onConfirm,
}: MoveToFolderModalProps) => {
  const { t } = useTranslation();
  // '' is the No-folder row, coerced to null on submit — the same convention as
  // CreateFormModal and the Setup tab's folder select.
  const [folderId, setFolderId] = useState('');

  // Preselect the forms' shared folder; a mixed selection has none, so it falls
  // back to No folder. `FormTemplate.folder` is a populated { id, title } ref.
  useEffect(() => {
    if (!open) return;
    const ids = new Set(forms.map((f) => f.folder?.id ?? ''));
    setFolderId(ids.size === 1 ? [...ids][0] : '');
  }, [open, forms]);

  const submit = () => {
    if (loading) return;
    onConfirm(folderId || null);
  };

  const row = (id: string, label: string) => {
    const on = folderId === id;
    return (
      <button
        key={id || 'none'}
        type='button'
        className={`forms-move-folder__row${on ? ' is-selected' : ''}`}
        aria-pressed={on}
        onClick={() => setFolderId(id)}
      >
        <Icon name='folder' size={18} className='forms-move-folder__row-icon' />
        <span className='forms-move-folder__row-title'>{label}</span>
        {on && <Icon name='check' size={16} className='forms-move-folder__row-check' />}
      </button>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='md'
      className='forms-move-folder'
      icon='folder-input'
      title={t('forms_action_move_folder')}
      subtitle={
        forms.length === 1 ? forms[0].name : t('forms_move_folder_count', { count: forms.length })
      }
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={loading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='check' loading={loading} onClick={submit}>
            {t('forms_move_folder_confirm')}
          </Button>
        </>
      }
    >
      <div className='forms-move-folder__list'>
        {/* No folder is always offered — it's how a form gets unfiled. */}
        {row('', t('forms_create_folder_none'))}
        {folders.map((folder) => row(folder.id, folder.title))}
      </div>
      {folders.length === 0 && (
        <p className='forms-move-folder__empty'>
          <Icon name='folder' size={15} />
          {t('forms_folders_modal_empty')}
        </p>
      )}
    </Modal>
  );
};

export default MoveToFolderModal;
